/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["draft"] }] */

import produce from 'immer';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { SeekerAction, SeekerState, SeekerInfo, JobPostSeekerState } from '@/types/seeker';
import { SENIOR_CARE_TYPE, YesOrNoAnswer, DistanceUnit } from '@/__generated__/globalTypes';
import { CARE_DATES } from '@/constants';
import { DEFAULT_AGE, DEFAULT_GENDER, DEFAULT_WHO_NEEDS_CARE } from '@/types/common';

dayjs.extend(isSameOrBefore);

export const jobPostInitialState: JobPostSeekerState = {
  selfNeedsCare: undefined,
  servicesNeeded: [],
  intent: undefined,
  lovedOne: {
    whoNeedsCare: DEFAULT_WHO_NEEDS_CARE,
    gender: DEFAULT_GENDER,
    age: DEFAULT_AGE,
    details: undefined,
  },
};

export const seekerInfoInitialState: SeekerInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

export const initialState: SeekerState = {
  careDate: CARE_DATES.WITHIN_A_WEEK,
  firstName: '',
  lastName: '',
  city: '',
  helpTypes: [],
  careAttributesSC: [],
  state: '',
  typeOfCare: SENIOR_CARE_TYPE.IN_HOME,
  zipcode: '',
  visitorStartedSACFlow: false,
  initialAccountCreationFailed: false,
  initialAccountCreationAttempted: false,
  caregiversNearbySearchRadius: undefined,
  gender: null,
  whoNeedsCare: null,
  whoNeedsCareAge: null,
  jobPost: jobPostInitialState,
  leadAndConnect: {
    acceptedProviders: [],
    maxDistanceFromSeeker: 10,
  },
  amenities: [],
  seekerInfo: seekerInfoInitialState,
  seniorCareFacilityLeadGenerateMutationError: false,
  fitsSALCBudget: YesOrNoAnswer.NOT_SURE,
  SALCSavedFacilitiesIds: [],
  SALCSeniorCareFacilityLeadsPublished: false,
  maxDistanceFromSeekerDayCare: {
    distance: 0,
    unit: DistanceUnit.MILES,
  },
  hdyhau: '',
  vertical: null,
};

export const reducer = produce((draft: SeekerState, action: SeekerAction) => {
  switch (action.type) {
    case 'setCityAndState':
      draft.city = action.city;
      draft.state = action.state;
      return draft;

    case 'setHelpTypes':
      draft.helpTypes = action.helpTypes;
      return draft;

    case 'setCareAttributesSC':
      draft.careAttributesSC = action.careAttributesSC;
      return draft;

    case 'setTypeOfCare':
      draft.typeOfCare = action.typeOfcare;
      return draft;

    case 'setZipcode':
      draft.zipcode = action.zipcode;
      return draft;

    case 'setLandingPageZipcode':
      draft.landingPageZipcode = action.landingPageZipcode;
      return draft;

    case 'setVisitorStartedSACFlow':
      draft.visitorStartedSACFlow = action.visitorStartedSACFlow;
      return draft;

    case 'setInitialAccountCreationFailed':
      draft.initialAccountCreationFailed = action.initialAccountCreationFailed;
      return draft;

    case 'setInitialAccountCreationAttempted':
      draft.initialAccountCreationAttempted = action.initialAccountCreationAttempted;
      return draft;

    case 'setCaregiversNearbySearchRadius':
      draft.caregiversNearbySearchRadius = action.caregiversNearbySearchRadius;
      return draft;

    case 'setWhoNeedsCare':
      draft.whoNeedsCare = action.whoNeedsCare;
      return draft;

    case 'setWhenNeedsCare':
      draft.whenNeedsCare = action.whenNeedsCare;
      return draft;

    case 'setWhoNeedsCareAge':
      draft.whoNeedsCareAge = action.age;
      return draft;

    case 'setWhoNeedsCareGender':
      draft.gender = action.gender;
      return draft;

    case 'setPaymentDetailTypes':
      draft.paymentDetailTypes = action.paymentDetailTypes;
      return draft;

    case 'setPaymentType':
      draft.paymentType = action.paymentType;
      return draft;

    case 'setSeekerName':
      if (action.firstName) {
        draft.firstName = action.firstName;
      }
      if (action.lastName) {
        draft.lastName = action.lastName;
      }
      return draft;

    case 'setFacilityBudgetQualification':
      draft.facilityBudgetQualification = action.facilityBudgetQualification;
      return draft;

    case 'setCondition':
      draft.condition = action.condition;
      return draft;

    case 'setNursingType':
      draft.nursingType = action.nursingType;
      return draft;

    case 'setAmenities':
      draft.amenities = action.amenities;
      return draft;

    case 'setSeekerInfo':
      draft.seekerInfo.firstName = action.firstName;
      draft.seekerInfo.lastName = action.lastName;
      draft.seekerInfo.email = action.email;
      draft.seekerInfo.phone = action.phone;
      return draft;

    case 'setSeniorCareFacilityLeadGenerateMutationError':
      draft.seniorCareFacilityLeadGenerateMutationError = action.value;
      return draft;

    case 'setFitsSALCBudget':
      draft.fitsSALCBudget = action.value;
      return draft;

    case 'setSALCSavedFacilitiesIds':
      draft.SALCSavedFacilitiesIds = action.SALCSavedFacilitiesIds;
      return draft;

    case 'setSALCTrackingId':
      draft.SALCTrackingId = action.SALCTrackingId;
      return draft;

    case 'setSALCCommunities':
      draft.SALCCommunities = action.SALCCommunities;
      return draft;

    case 'setSeniorAssistedLivingCenterFacilityCount':
      draft.assistedLivingCenterFacilityCount = action.assistedLivingCenterFacilityCount;
      return draft;

    case 'setCaringFacilityCountNearBy':
      draft.caringFacilityCountNearBy = action.caringFacilityCountNearBy;
      return draft;

    case 'setWhenLookingToMove':
      draft.whenLookingToMove = action.whenLookingToMove;
      return draft;

    case 'setRecommendedHelpType':
      draft.recommendedHelpType = action.recommendedHelpType;
      return draft;

    // PAJ;
    case 'setLovedOneAge':
      draft.jobPost.lovedOne.age = action.age;
      return draft;

    case 'setSeekerParams': {
      draft.jobPost.selfNeedsCare = false;
      draft.jobPost.intent = 'UNDECIDED';
      draft.jobPost.servicesNeeded = action.servicesNeeded;
      return draft;
    }

    // Lead and connect
    case 'setAcceptedProviders':
      draft.leadAndConnect.acceptedProviders = action.acceptedProviders;
      return draft;

    case 'setSkippedCount':
      draft.leadAndConnect.skippedCount = action.skippedCount;
      return draft;

    case 'setInitialProviderSeen':
      draft.leadAndConnect.initialProviderSeen = action.initialProviderSeen;
      return draft;

    case 'setMaxDistanceFromSeeker':
      draft.leadAndConnect.maxDistanceFromSeeker = action.maxDistanceFromSeeker;
      return draft;

    case 'setMessageSentAttempts':
      draft.leadAndConnect.messageSentAttempts = action.messageSentAttempts;
      return draft;

    case 'setFailedMessageSentProviderIds':
      draft.leadAndConnect.failedMessageSentProviderIds = action.failedMessageSentProviderIds;
      return draft;

    case 'setMessage':
      draft.leadAndConnect.message = action.message;
      return draft;

    case 'setActionedProviders':
      draft.leadAndConnect.actionedProviders = action.actionedProviders;
      return draft;

    case 'setSALCSeniorCareFacilityLeadsPublished':
      draft.SALCSeniorCareFacilityLeadsPublished = action.SALCSeniorCareFacilityLeadsPublished;
      return draft;

    case 'setMaxDistanceFromSeekerDayCare':
      draft.maxDistanceFromSeekerDayCare = { distance: action.distance, unit: action.unit };
      return draft;

    case 'setHdyhau':
      draft.hdyhau = action.hdyhau;
      return draft;

    case 'setVertical':
      draft.vertical = action.vertical;
      return draft;

    case 'setIsUserAssignedToSkipSteps':
      draft.isUserAssignedToSkipSteps = action.isAssigned;
      return draft;

    case 'setSource':
      draft.source = action.source;
      return draft;

    case 'setSourceType':
      draft.sourceType = action.sourceType;
      return draft;

    case 'setQueryParamMetadata':
      draft.queryParamMetadata = action.queryParamMetadata;
      return draft;

    case 'setDataTransferMetadata':
      draft.dataTransferMetadata = action.dataTransferMetadata;
      return draft;

    case 'setZipMissingMetaData':
      draft.zipMissingMetaData = action.zipMissingMetaData;
      return draft;

    case 'setNumOfProviders':
      draft.numOfProviders = action.numOfProviders;
      return draft;

    case 'setCzenServiceIdForSeniorCareMember':
      draft.czenServiceIdForMember = action.serviceIdForMember;
      return draft;

    default:
      return draft;
  }
});
